import cloneDeep from 'lodash/cloneDeep';
import findIndex from 'lodash/findIndex';

import { appContactToKeyed, IUDBKeyedContact } from '../../lib/ContactsDatabase';
import { hasContactId } from '../../core/libs/utils';
import { IUContactsActionType } from '../../contexts/ContactsContext';

import { TAppContact } from 'TProtocol/common/models';
import { UAppContact } from '../../models/UAppContact';

export interface IContactsState {
  clientModificationId: string | undefined;
  contacts: IUDBKeyedContact[];
  archivedContacts: IUDBKeyedContact[];
  selfContact: TAppContact | undefined;
  contactsLoaded: boolean;
  homepageLoaded: boolean;
  searchLoadProgress: number;
  LCM: Map<string, TAppContact[]>;
  visibleContactsMap: Map<string, TAppContact>;
  selectedContact: TAppContact | undefined;
  contactToEdit: UAppContact | undefined;
  updatingContact: TAppContact | undefined;
  error: boolean;
}

type IReducerAction =
  { type: 'clear' }
  | { type: 'editContact', contact?: UAppContact }
  | { type: 'error' }
  | { type: 'loadComplete', contacts: IUDBKeyedContact[], clientModificationId?: string }
  | { type: 'loadSuccess', contacts: IUDBKeyedContact[], selfContact?: TAppContact, archivedContacts: IUDBKeyedContact[] }
  | { type: 'homepageComplete', error: boolean, LCM: Map<string, TAppContact[]> }
  | { type: 'selectContact', selectedContact: TAppContact }
  | { type: 'setLCM', LCM: Map<string, TAppContact[]> }
  | { type: 'setVisibleContactsMap', visibleContactsMap: Map<string, TAppContact> }
  | { type: 'updatedContact', contact: TAppContact, actionType: IUContactsActionType }
  | { type: 'updateSearchProgress', progress: number }
  | { type: 'updatingContact', contact: TAppContact };

const updatedContactReducer = (
  state: IContactsState,
  action: { type: 'updatedContact', contact: TAppContact, actionType: IUContactsActionType }
): IContactsState => {
  const contact = action.contact;

  if (!hasContactId(contact)) {
    return state;
  }

  const contacts = [...state.contacts];
  const visibleContactsMap = state.visibleContactsMap;
  const existingContactIndex = findIndex(state.contacts, { contactId: contact.contactId });

  if (existingContactIndex !== -1) {
    if (action.actionType === IUContactsActionType.Delete) {
      contacts.splice(existingContactIndex, 1);
    } else if (action.actionType === IUContactsActionType.Add) {
      // During an Add action, the contact is added earlier in the process, so all we need to do is clear
      // updatingContact.
    } else {
      contacts[existingContactIndex] = appContactToKeyed(contact);
      visibleContactsMap.set(contact.contactId, contact);
    }
  }

  let selfContact = state.selfContact;
  if (contact.contactId === state.selfContact?.contactId) {
    selfContact = contact;
  }

  return { ...state, contacts, visibleContactsMap, updatingContact: undefined, selfContact };
};

export const ContactsReducer = (state: IContactsState, action: IReducerAction): IContactsState => {
  switch (action.type) {
    case 'clear':
      return { ...state, contacts: [], LCM: new Map() };
    case 'editContact':
      if (action.contact === undefined) {
        return { ...state, contactToEdit: undefined };
      } else if (action.contact?.isUAppContact) {
        return { ...state, contactToEdit: cloneDeep(action.contact) };
      } else {
        return { ...state, contactToEdit: new UAppContact(cloneDeep(action.contact)) };
      }
    case 'error':
      return { ...state, error: true };
    case 'homepageComplete':
      return { ...state, homepageLoaded: true, error: action.error, LCM: action.LCM };
    case 'loadComplete':
      return {
        ...state,
        contacts: action.contacts,
        contactsLoaded: true,
        clientModificationId: action.clientModificationId
      };
    case 'loadSuccess':
      return {
        ...state,
        contacts: action.contacts,
        selfContact: action.selfContact,
        archivedContacts: action.archivedContacts
      };
    case 'selectContact':
      return { ...state, selectedContact: action.selectedContact };
    case 'setLCM':
      return { ...state, LCM: action.LCM };
    case 'setVisibleContactsMap':
      return { ...state, visibleContactsMap: action.visibleContactsMap };
    case 'updatedContact':
      return updatedContactReducer(state, action);
    case 'updateSearchProgress':
      return { ...state, searchLoadProgress: action.progress };
    case 'updatingContact':
      return { ...state, contactToEdit: undefined, updatingContact: action.contact };
    default:
      return state;
  }
};
